import { useRef } from 'react';
import { Editor } from '@tiptap/core';

import ToolbarButton from '@modules/documents/components/header/toolbar/ToolbarButton';
import ToolbarMenuButton from '@modules/documents/components/header/toolbar/ToolbarMenuButton';
import ToolbarSplitButton from '@modules/documents/components/header/toolbar/ToolbarSplitButton';
import { TIcon } from '@shared/types';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { ChevronDown12Regular, PaintBucketRegular } from '@fluentui/react-icons';

type TProps = {
  editor: Editor;
  showTooltip: boolean;
  isBubble?: boolean;
};

const FALLBACK_COLOR = '#f2f2f2';

const makeSwatchIcon = (color: string): TIcon => ({
  style: {
    display: 'inline-flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 1,
  },
  children: (
    <>
      <PaintBucketRegular />
      <span
        style={{
          width: 14,
          height: 3,
          borderRadius: 9999,
          backgroundColor: color || FALLBACK_COLOR,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
        }}
      />
    </>
  ),
});

const ColorCellButton = ({ editor, showTooltip, isBubble }: TProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isHeaderCell = useEditorActive((ed) => ed.isActive('tableHeader'));
  const currentColor = useEditorActive((ed) => {
    const cell = ed.getAttributes('tableCell')?.backgroundColor;
    const header = ed.getAttributes('tableHeader')?.backgroundColor;
    return (cell || header || '') as string;
  });

  // Shared by the toolbar and the floating table menu through the editor's storage.
  const lastColor = useEditorActive((ed) => ed.storage.table?.lastCellColor ?? FALLBACK_COLOR);

  const applyColor = (hex: string) => {
    if (isHeaderCell) return;
    // Before the transaction, so the swatch re-renders with the new color.
    editor.storage.table.lastCellColor = hex;
    editor.chain().focus().setCellAttribute('backgroundColor', hex).run();
  };

  const openPicker = () => {
    if (!inputRef.current) return;
    inputRef.current.value = currentColor || lastColor;

    if (inputRef.current.showPicker) {
      inputRef.current.showPicker();
    } else {
      inputRef.current.click();
    }
  };

  const tooltipFor = (label: string) =>
    showTooltip ? (isHeaderCell ? 'Toggle off the header to color this cell.' : label) : undefined;

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <ToolbarSplitButton
        isChevronDisabled={isHeaderCell}
        renderMain={(className) => (
          <ToolbarButton
            className={className}
            icon={makeSwatchIcon(lastColor)}
            tooltip={tooltipFor('Cell color')}
            disabled={(e) => e.isActive('tableHeader')}
            onClick={() => applyColor(lastColor)}
          />
        )}
        renderChevron={(className) => (
          <ToolbarMenuButton
            className={className}
            menuIcon={<ChevronDown12Regular />}
            tooltip={tooltipFor('Choose cell color')}
            disabled={isHeaderCell}
            onClick={openPicker}
          />
        )}
      />

      <input
        ref={inputRef}
        type="color"
        aria-label="Pick cell background color"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
        onChange={(e) => {
          const hex = e.target.value;
          if (hex) applyColor(hex);
        }}
      />
    </span>
  );
};

export default ColorCellButton;
